import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'uikit-tutorial-progress';

interface TutorialProgress {
  completedPages: string[];
  lastVisited: string | null;
}

const getInitialProgress = (): TutorialProgress => {
  if (typeof window === 'undefined') {
    return { completedPages: [], lastVisited: null };
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load tutorial progress:', e);
  }

  return { completedPages: [], lastVisited: null };
};

export const useTutorialProgress = () => {
  const [progress, setProgress] = useState<TutorialProgress>(getInitialProgress);

  // Save to localStorage whenever progress changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (e) {
      console.error('Failed to save tutorial progress:', e);
    }
  }, [progress]);

  const markAsComplete = useCallback((pageId: string) => {
    setProgress(prev => {
      if (prev.completedPages.includes(pageId)) {
        return prev;
      }
      return {
        ...prev,
        completedPages: [...prev.completedPages, pageId],
      };
    });
  }, []);

  const markAsIncomplete = useCallback((pageId: string) => {
    setProgress(prev => ({
      ...prev,
      completedPages: prev.completedPages.filter(id => id !== pageId),
    }));
  }, []);

  const toggleComplete = useCallback((pageId: string) => {
    setProgress(prev => {
      if (prev.completedPages.includes(pageId)) {
        return {
          ...prev,
          completedPages: prev.completedPages.filter(id => id !== pageId),
        };
      }
      return {
        ...prev,
        completedPages: [...prev.completedPages, pageId],
      };
    });
  }, []);

  const isCompleted = useCallback((pageId: string) => {
    return progress.completedPages.includes(pageId);
  }, [progress.completedPages]);

  const setLastVisited = useCallback((path: string) => {
    setProgress(prev => ({
      ...prev,
      lastVisited: path,
    }));
  }, []);

  const getCompletedCount = useCallback(() => {
    return progress.completedPages.length;
  }, [progress.completedPages]);

  const resetProgress = useCallback(() => {
    setProgress({ completedPages: [], lastVisited: null });
  }, []);

  return {
    completedPages: progress.completedPages,
    lastVisited: progress.lastVisited,
    markAsComplete,
    markAsIncomplete,
    toggleComplete,
    isCompleted,
    setLastVisited,
    getCompletedCount,
    resetProgress,
  };
};
